/**
 * What 3 Ingredients - Recipe-Encoded Deep Link Handler
 * Allows exact, deterministic sharing of generated recipes via URL query parameters.
 */

/**
 * Encodes a complete recipe object into a URL-safe query parameter and updates browser history
 * @param {object} recipe 
 * @param {number} servings 
 * @returns {string} full shareable URL
 */
export function encodeRecipeToURL(recipe, servings = 2) {
  if (!recipe) return window.location.href;

  try {
    const compact = {
      t: recipe.title,
      g: recipe.tagline,
      r: recipe.region || 'north-america',
      u: recipe.unitSystem || 'imperial',
      m: recipe.mainIngredients || [],
      bq: recipe.baseQuantities || {},
      ut: recipe.utilityIngredients || [],
      p: recipe.prepTime || '5 mins',
      c: recipe.cookTime || '15 mins',
      st: recipe.steps || [],
      tip: recipe.chefTip || ''
    };

    const jsonStr = JSON.stringify(compact);
    const encoded = btoa(encodeURIComponent(jsonStr));

    const newUrl = `${window.location.origin}${window.location.pathname}?r=${encoded}&s=${servings}`;
    
    window.history.replaceState({ recipeId: recipe.id }, recipe.title, newUrl);
    
    return newUrl;
  } catch (err) {
    console.warn('Could not encode recipe to URL', err);
    return window.location.href;
  }
}

/**
 * Reads URL search parameters on page boot.
 * @returns {{ type: 'encoded', recipe: object, servings: number } | { type: 'ingredients', ingredientsText: string, region: string, servings: number, unitSystem: string } | null}
 */
export function decodeRecipeFromURL() {
  const params = new URLSearchParams(window.location.search);
  const rParam = params.get('r');
  const sParam = parseInt(params.get('s') || '2', 10);
  const servings = isNaN(sParam) ? 2 : Math.max(1, Math.min(8, sParam));

  if (rParam) {
    try {
      const decodedStr = decodeURIComponent(atob(rParam));
      const raw = JSON.parse(decodedStr);

      const recipe = {
        id: 'shared-' + Date.now(),
        title: raw.t,
        tagline: raw.g,
        region: raw.r,
        unitSystem: raw.u || 'imperial',
        mainIngredients: raw.m,
        baseQuantities: raw.bq,
        utilityIngredients: raw.ut || raw.u,
        prepTime: raw.p,
        cookTime: raw.c,
        servings: 2,
        steps: raw.st,
        chefTip: raw.tip
      };

      return { type: 'encoded', recipe, servings };
    } catch (err) {
      console.warn('Failed to decode recipe from URL parameter', err);
    }
  }

  const iParam = params.get('i') || params.get('ingredients');
  const regParam = params.get('reg') || params.get('region') || 'all';
  const uParam = params.get('u') || 'imperial';
  if (iParam) {
    return {
      type: 'ingredients',
      ingredientsText: decodeURIComponent(iParam),
      region: regParam,
      servings,
      unitSystem: uParam
    };
  }

  return null;
}
